import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { colors, spacing } from '../theme';
import Card from '../components/Card';
import SearchBar from '../components/SearchBar';
import Table from '../components/Table';
import EmptyState from '../components/EmptyState';
import SkeletonLoader from '../components/SkeletonLoader';
import { supabase } from '../lib/supabase';

function PeopleAccounts() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!supabase) return;
      setLoading(true);
      let query = supabase
        .from('clients')
        .select('id, login_email, owner_email, business_email, business_name, owner_name, account_state, user_id, activated_at, created_at, nwra_company_id')
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(100);

      if (stateFilter !== 'all') {
        query = query.eq('account_state', stateFilter);
      }
      if (search.trim()) {
        const term = search.trim();
        query = query.or(
          `login_email.ilike.%${term}%,owner_email.ilike.%${term}%,business_email.ilike.%${term}%,business_name.ilike.%${term}%,owner_name.ilike.%${term}%`
        );
      }

      const { data, error } = await query;
      if (!cancelled) {
        if (error) {
          setClients([]);
        } else {
          setClients(data || []);
        }
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [search, stateFilter]);

  const columns = [
    {
      key: 'identity',
      label: 'Person / Account',
      render: (row) => (
        <div>
          <div style={{ fontWeight: 600 }}>
            {row.business_name || row.owner_name || row.login_email || `Client #${row.id}`}
          </div>
          <div style={{ fontSize: '0.85rem', color: colors.midGray }}>
            {row.login_email || row.owner_email || row.business_email || 'No email'}
          </div>
        </div>
      ),
    },
    {
      key: 'account_state',
      label: 'State',
      render: (row) => (
        <span
          style={{
            textTransform: 'capitalize',
            fontWeight: 600,
            color: row.account_state === 'dormant' ? '#b45309' : colors.darkGray,
          }}
        >
          {row.account_state || 'active'}
        </span>
      ),
    },
    {
      key: 'links',
      label: 'Links',
      render: (row) => (
        <span style={{ fontSize: '0.85rem' }}>
          {row.user_id ? 'Portal user' : 'No login'}
          {row.nwra_company_id ? ' · NWRA' : ''}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <Link to={`/admin/matching/accounts/${row.id}`} style={{ color: colors.blue, fontWeight: 600 }}>
          View profile
        </Link>
      ),
    },
  ];

  return (
    <Card title="People / Accounts">
      <div style={{ display: 'flex', gap: spacing.md, flexWrap: 'wrap', marginBottom: spacing.md }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <SearchBar value={search} onChange={setSearch} placeholder="Search people…" />
        </div>
        <select
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          style={{ padding: '0.5rem', borderRadius: 8, border: `1px solid ${colors.gray200}` }}
        >
          <option value="all">All states</option>
          <option value="active">Active</option>
          <option value="dormant">Dormant</option>
          <option value="disabled">Disabled</option>
        </select>
      </div>
      {loading ? (
        <SkeletonLoader variant="table" count={5} />
      ) : clients.length === 0 ? (
        <EmptyState title="No accounts found" description="Try a different search or filter." />
      ) : (
        <Table columns={columns} data={clients} keyField="id" responsive />
      )}
    </Card>
  );
}

export default PeopleAccounts;
